import React, { useState } from 'react';
import { Button, Menu, MenuItem, Checkbox, ListItemText, Divider, Box, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { REQUISITION_OPTIONS } from '../hooks/useMappingDepartments';

export function RequisitionDropdown({ value, onChange, deptNumber }) {
  const [anchorEl, setAnchorEl] = useState(null);
  // draft = what user has ticked but not yet applied
  const [draft, setDraft] = useState([]);
  const open = Boolean(anchorEl);

  const committed = value
    ? value
        .split('|')
        .map(v => v.trim())
        .filter(Boolean)
    : [];

  const handleOpen = e => {
    e.stopPropagation();
    setDraft(committed); // seed draft with current committed value
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setDraft([]);
    setAnchorEl(null);
  };

  const toggleDraft = opt => {
    setDraft(prev => (prev.includes(opt) ? prev.filter(s => s !== opt) : [...prev, opt]));
  };

  const handleApply = e => {
    e.stopPropagation();
    onChange(deptNumber, draft.join(' | '));
    setAnchorEl(null);
  };

  const handleClear = e => {
    e.stopPropagation();
    setDraft([]);
  };

  const label = committed.length === 0 ? 'None' : committed.length === 1 ? committed[0] : `${committed.length} selected`;

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        onClick={handleOpen}
        variant="outlined"
        size="small"
        endIcon={<KeyboardArrowDownIcon sx={{ color: '#64748b', fontSize: '16px !important' }} />}
        startIcon={
          <LocalOfferIcon sx={{ color: committed.length > 0 ? '#6366f1' : '#94a3b8', fontSize: '14px !important' }} />
        }
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          color: committed.length > 0 ? '#0f172a' : '#64748b',
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          fontSize: '13px',
          fontWeight: committed.length > 0 ? 600 : 400,
          px: 1.5,
          py: 0.5,
          borderRadius: '7px',
          width: '100%',
          '&:hover': { backgroundColor: '#f8fafc', borderColor: 'transparent' },
          '& .MuiButton-endIcon': { marginLeft: 'auto' },
        }}>
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {label}
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        onClick={e => e.stopPropagation()}
        PaperProps={{
          elevation: 0,
          sx: {
            mt: 0.75,
            minWidth: 230,
            borderRadius: '12px',
            border: '1px solid #f1f5f9',
            boxShadow: '0 12px 30px -4px rgba(15, 23, 42, 0.08)',
            p: 0.5,
          },
        }}>
        {/* Options list */}
        {REQUISITION_OPTIONS.map(opt => {
          const checked = draft.includes(opt);
          return (
            <MenuItem
              key={opt}
              onClick={e => {
                e.stopPropagation();
                toggleDraft(opt);
              }}
              sx={{
                borderRadius: '6px',
                py: 0.75,
                px: 1,
                mb: 0.25,
                backgroundColor: checked ? 'rgba(99, 102, 241, 0.04)' : 'transparent',
                '&:hover': { backgroundColor: checked ? 'rgba(99, 102, 241, 0.08)' : '#f8fafc' },
              }}>
              <Checkbox
                checked={checked}
                size="small"
                sx={{ p: 0.5, mr: 1, color: '#cbd5e1', '&.Mui-checked': { color: '#6366f1' } }}
              />
              <ListItemText
                primary={opt}
                primaryTypographyProps={{
                  fontSize: '13px',
                  fontWeight: checked ? 600 : 400,
                  color: checked ? '#0f172a' : '#475569',
                }}
              />
            </MenuItem>
          );
        })}

        {/* Footer: Clear + Apply */}
        <Divider sx={{ my: 0.5, borderColor: '#f1f5f9' }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.75, gap: 1 }}>
          <Button
            size="small"
            onClick={handleClear}
            disabled={draft.length === 0}
            variant="text"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              color: '#ef4444',
              fontWeight: 600,
              px: 1,
              minWidth: 0,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&.Mui-disabled': {
                color: '#fca5a5',
                backgroundColor: 'transparent',
                border: 'none',
              },
            }}>
            Clear
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleApply}
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              px: 2,
              borderRadius: '6px',
              bgcolor: '#0f172a',
              '&:hover': { bgcolor: '#1e293b' },
            }}>
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
}
